import React, {useState, useMemo, useCallback} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import Button from '../../../../../../../Common/BaseComponentWrappers/MotifButton';
import {labels} from '../../../../../../../../util/utils';
import {raiseJSValidationError} from '../../../../../../../../actions/erroractions';
import {independenceStatus, roles} from '../../../../../../../../util/uiconstants';
import IndependenceModal from '../../../../../../../Common/CanvasForm/IndependenceModal/IndependenceModal';
import LoadingIndicator from '../../../../../../../Common/LoadingIndicator/LoadingIndicator';
import styled from 'styled-components';

export default function AccessButtons(props) {
	const {doc} = props;

	const [showModal, setShowModal] = useState(false),
		[btnType, setBtnType] = useState(),
		[showLoading, setShowLoading] = useState(false);

	const userPreferences = useSelector((state) => state.userPreferences);

	const independenceComments = useSelector((state) => {
		let val = Object.keys(state.internalUser).length > 0 ? state.internalUser.independenceInformation : [];

		if (doc.externalStreamId !== userPreferences.id) {
			val = doc.independenceInformation;
		}

		return val.comments || '';
	});

	const handleOnClick = useCallback((type) => {
		setBtnType(type);
		setShowModal(true);
	}, []);

	const independenceModal = useMemo(() => {
		return (
			<IndependenceModal
				showModal
				independenceComments={independenceComments}
				btnType={typeLabels[btnType]}
				setShowIndependenceModal={setShowModal}
				setShowIndependenceLoading={setShowLoading}
				setActiveIndex={(type) => setBtnType(type)}
			/>
		);
	}, [btnType, independenceComments]);

	return (
		<AccessButtonsWrapper>
			<LoadingIndicator show={showLoading} styleName="independenceLoader" />
			<section className={'independenceAccessButtons'}>
				<OverrideButton doc={doc} userPreferences={userPreferences} onClick={handleOnClick} btnType={btnType} />
				<GrantAccessButton doc={doc} userPreferences={userPreferences} onClick={handleOnClick} btnType={btnType} />
				<DenyAccessButton doc={doc} userPreferences={userPreferences} onClick={handleOnClick} btnType={btnType} />
			</section>
			{showModal && <React.Fragment>{independenceModal}</React.Fragment>}
		</AccessButtonsWrapper>
	);
}

const typeLabels = {
	1: labels.override,
	2: labels.grantAccess,
	3: labels.denyAccess
};

const grandDenyStatuses = [
	independenceStatus.MATTERIDENTIFIEDPENDINGACTION,
	independenceStatus.MATTERRESOLVEDDENIEDACCESS,
	independenceStatus.MATTERRESOLVEDGRANTEDACCESS,
	independenceStatus.PRIORMATTERRESOLVEDDENIEDACCESS,
	independenceStatus.PRIORMATTERRESOLVEDGRANTEDACCESS,
	independenceStatus.PRIORMATTERIDENTIFIEDPENDINGACTION
];

function AccessButton(props) {
	const {variant, buttonLabel, onClick, userPreferences, isActive} = props;

	const dispatch = useDispatch();

	const disabled =
		[roles.PARTNERINCHARGE, roles.ENGAGEMENTPARTNER, roles.EXECUTIVEDIRECTOR].indexOf(
			userPreferences.engagementRoleId
		) === -1;

	const handleOnClick = (type) => {
		if (disabled) {
			dispatch(raiseJSValidationError(labels.independenceHoverText));
		} else {
			onClick && onClick(type);
		}
	};

	return (
		<Button
			showLabel
			variant={variant}
			buttonLabel={buttonLabel}
			onClick={handleOnClick}
			className={isActive ? 'motif-active' : ''}
			size="medium"
		/>
	);
}

function OverrideButton(props) {
	const {doc, userPreferences, onClick, btnType} = props;

	const show = useMemo(() => {
		let overridableStatuses = [independenceStatus.INCOMPLETE, independenceStatus.OVERRIDDEN];
		return (
			doc.externalStreamId !== userPreferences.id &&
			overridableStatuses.indexOf(doc?.independenceInformation?.independenceStatusId) > -1
		);
	}, [doc]);

	return (
		<React.Fragment>
			{show && (
				<AccessButton
					variant="primary"
					buttonLabel={labels.override}
					onClick={() => onClick(1)}
					userPreferences={userPreferences}
					isActive={btnType === 1}
				/>
			)}
		</React.Fragment>
	);
}

function GrantAccessButton(props) {
	const {doc, userPreferences, onClick, btnType} = props;

	const show = useMemo(() => {
		return (
			doc.externalStreamId !== userPreferences.id &&
			grandDenyStatuses.indexOf(doc?.independenceInformation?.independenceStatusId) > -1
		);
	}, [doc]);

	return (
		<React.Fragment>
			{show && (
				<AccessButton
					variant="primary"
					buttonLabel={labels.grantAccess}
					onClick={() => onClick(2)}
					userPreferences={userPreferences}
					isActive={btnType === 2}
				/>
			)}
		</React.Fragment>
	);
}

function DenyAccessButton(props) {
	const {doc, userPreferences, onClick, btnType} = props;

	const show = useMemo(() => {
		return (
			doc.externalStreamId !== userPreferences.id &&
			grandDenyStatuses.indexOf(doc?.independenceInformation?.independenceStatusId) > -1
		);
	}, [doc]);

	return (
		<React.Fragment>
			{show && (
				<AccessButton
					variant="secondary"
					buttonLabel={labels.denyAccess}
					onClick={() => onClick(3)}
					userPreferences={userPreferences}
					isActive={btnType === 3}
				/>
			)}
		</React.Fragment>
	);
}

const AccessButtonsWrapper = styled.section`
	.independenceAccessButtons {
		display: flex;
		gap: var(--px-24);
	}
`;
